import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import formaPagamentoService from '../../services/formaPagamentoService';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import Toast from '../../components/Toast';

const pageSizes = [10, 20, 50, 100];

const FormaPagamentoList = () => {
  const [filters, setFilters] = useState({ nome: '', status: '' });
  const [formas, setFormas] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [loading, setLoading] = useState(false);

  const columns = [
    { Header: 'ID', accessor: 'idFormaPagamento' },
    { Header: 'Nome', accessor: 'nome' },
    { Header: 'Status', accessor: 'status' },
    { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
    { Header: 'Alterado Por', accessor: 'alteradoPor' },
    { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => value ? new Date(value).toLocaleString() : '' },
    { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => value ? new Date(value).toLocaleString() : '' }
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    columns
      .filter(col => !['cadastradoPor', 'alteradoPor', 'cadastradoEm', 'alteradoEm'].includes(col.accessor))
      .map(col => col.accessor)
  );

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const loadFormas = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...filters
      };
      const response = await formaPagamentoService.list(params);
      setFormas(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Erro ao carregar formas de pagamento:', error);
      showToast('Erro ao carregar formas de pagamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadFormas();
  }, [loadFormas]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Formas de Pagamento</h2>
            <Link
              to="/forma-pagamento/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Forma</span>
            </Link>
          </div>
          <div className="max-w-full mb-6">
            {/* Filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                name="nome"
                value={filters.nome}
                onChange={handleFilterChange}
                placeholder="Nome da Forma de Pagamento"
                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 min-w-0"
              />
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 min-w-0"
              >
                <option value="">Todos Status</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
            </div>
          </div>
          {/* Controles */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
              <div className="order-2 sm:order-1 w-full sm:w-auto">
                {pagination && (
                  <Pagination 
                    currentPage={pagination.page || 1}
                    totalPages={pagination.pages || 1}
                    onPageChange={setPage}
                  />
                )}
              </div>
              <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
                <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                  onClick={handleOpenModal}
                  aria-label="Exibir/Ocultar colunas"
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>Colunas</span>
              </button>
            </div>
          </div>
          <ColumnVisibilityModal
              columns={columns}
              visibleColumns={visibleColumns}
              onChange={handleToggleColumn}
              onClose={handleCloseModal}
              isOpen={isModalOpen}
          />
          {loading ? (
            <div className="text-center py-4">Carregando...</div>
          ) : (
            <Table
                columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/forma-pagamento/${row.idFormaPagamento}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={formas}
            />
          )}
        </div>
      </div>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default FormaPagamentoList;
